import { trpc } from './trpc';

// ============================================================================
// Types
// ============================================================================

// Funnel type definition
export interface Funnel {
    id: string;
    name: string;
    description: string;
    storeId: string;
    stepsCount: number;
    stepTypes?: string[];
    conversionRate?: number | null;
    status: string; // Changed to string to accept any status from API
    isDefault?: boolean;
    /** Custom domains this funnel is promoted/live on. Non-empty => live. */
    productionDomains?: string[];
    createdAt: string;
    updatedAt: string;
}

// Hook for listing funnels
export function useFunnelsList(storeId: string, params?: any) {
    const result = trpc.funnels.list.useQuery(
        { storeId },
        {
            enabled: !!storeId,
            staleTime: 30000, // 30 seconds
        }
    );

    // Transform the result to match expected format with pagination
    // Note: Backend currently returns flat array, frontend expects wrapped response
    // TODO: Add proper pagination, filtering, sorting to backend
    return {
        ...result,
        data: result.data ? {
            funnels: result.data,
            total: result.data.length,
            pageCount: Math.ceil(result.data.length / (params?.pagination?.pageSize || 10)),
        } : undefined,
    };
}

// Hook for getting a specific funnel
export function useFunnel(funnelId: string, storeId: string) {
    return trpc.funnels.getById.useQuery(
        { funnelId, storeId },
        {
            enabled: !!funnelId && !!storeId,
        }
    );
}

// Hook for getting the default funnel
export function useDefaultFunnel(storeId: string) {
    return trpc.funnels.getDefault.useQuery(
        { storeId },
        {
            enabled: !!storeId,
        }
    );
}

// Hook for creating a funnel
export function useCreateFunnel() {
    const utils = trpc.useUtils();
    
    return trpc.funnels.create.useMutation({
        onSuccess: () => {
            // Invalidate funnel list to refresh the table
            utils.funnels.list.invalidate();
        },
    });
}

// Hook for creating a default simple funnel
export function useCreateDefaultFunnel() {
    const utils = trpc.useUtils();
    
    return trpc.funnels.createDefault.useMutation({
        onSuccess: () => {
            // Invalidate funnel list to refresh the table
            utils.funnels.list.invalidate();
        },
    });
}

// Hook for updating a funnel
export function useUpdateFunnel() {
    const utils = trpc.useUtils();
    
    return trpc.funnels.update.useMutation({
        onSuccess: () => {
            // Invalidate both list and individual funnel queries
            utils.funnels.list.invalidate();
            utils.funnels.getById.invalidate();
            utils.funnels.getDefault.invalidate();
        },
    });
}

// Hook for setting a funnel as default
export function useSetDefaultFunnel() {
    const utils = trpc.useUtils();
    
    return trpc.funnels.update.useMutation({
        onSuccess: () => {
            // Invalidate both list and default funnel queries
            utils.funnels.list.invalidate();
            utils.funnels.getDefault.invalidate();
        },
    });
}

// Hook for updating a funnel with automatic routing operations
export function useUpdateFunnelWithRouting() {
    const utils = trpc.useUtils();
    
    return trpc.funnels.updateWithRouting.useMutation({
        onSuccess: () => {
            // Invalidate both list and individual funnel queries
            utils.funnels.list.invalidate();
            utils.funnels.getById.invalidate();
            // Invalidate update checker and discrepancy checker (if available)
            try {
                utils.pluginUpdates?.checkFunnelUpdates?.invalidate();
                utils.pluginUpdates?.detectFunnelDiscrepancies?.invalidate();
            } catch (e) {
                console.warn('Could not invalidate plugin update queries:', e);
            }
        },
    });
}

// Hook for deleting a funnel
export function useDeleteFunnel() {
    const utils = trpc.useUtils();
    
    return trpc.funnels.delete.useMutation({
        onSuccess: () => {
            // Invalidate funnel list to refresh the table
            utils.funnels.list.invalidate();
        },
    });
}

// Hook for bulk deleting funnels
export function useBulkDeleteFunnels() {
    const utils = trpc.useUtils();
    
    return trpc.funnels.bulkDelete.useMutation({
        onSuccess: () => {
            // Invalidate funnel list to refresh the table
            utils.funnels.list.invalidate();
        },
    });
}

// Hook for duplicating a funnel
export function useDuplicateFunnel() {
    const utils = trpc.useUtils();

    return trpc.funnels.duplicate.useMutation({
        onSuccess: () => {
            // Invalidate funnel list to refresh the table
            utils.funnels.list.invalidate();
        },
    });
}

// Hook for validating a funnel
export function useValidateFunnel(config: any) {
    return trpc.funnels.validate.useQuery(
        { config },
        {
            enabled: !!config,
        }
    );
}

// Hook for promoting funnel to production
export function usePromoteFunnelToProduction() {
    const utils = trpc.useUtils();
    
    return trpc.funnels.promoteToProduction.useMutation({
        onSuccess: () => {
            utils.funnels.getById.invalidate();
        },
    });
}

// Hook for demoting funnel to staging
export function useDemoteFunnelToStaging() {
    const utils = trpc.useUtils();
    
    return trpc.funnels.demoteToStaging.useMutation({
        onSuccess: () => {
            utils.funnels.getById.invalidate();
        },
    });
}

// ============================================================================
// Template Hooks
// ============================================================================

// Funnel template type (matches backend FunnelTemplateWithStatus)
export interface FunnelTemplate {
    id: string;
    name: string;
    description: string;
    icon: string;
    requirements: string[];
    tags: string[];
    isDefault?: boolean;
    available: boolean;
    unavailableReason?: string;
}

// Hook for listing funnel templates with availability status
export function useFunnelTemplates(storeId: string) {
    return trpc.funnels.listTemplates.useQuery(
        { storeId },
        {
            enabled: !!storeId,
            staleTime: 60000, // 1 minute (templates don't change often)
        }
    );
}

// Hook for creating a funnel from a template
export function useCreateFunnelFromTemplate() {
    const utils = trpc.useUtils();
    
    return trpc.funnels.createFromTemplate.useMutation({
        onSuccess: () => {
            utils.funnels.list.invalidate();
        },
    });
}

export function useLaunchSetupCheckout() {
    const utils = trpc.useUtils();

    return trpc.funnels.launchSetupCheckout.useMutation({
        onSuccess: () => {
            utils.funnels.list.invalidate();
        },
    });
}

export function useExportFunnelBundle() {
    return trpc.funnels.exportBundle.useMutation();
}

export function useImportFunnelBundle() {
    const utils = trpc.useUtils();

    return trpc.funnels.importBundle.useMutation({
        onSuccess: () => {
            utils.funnels.list.invalidate();
        },
    });
}

/**
 * Polls the step of a running bundle import. Pass the same jobId given to
 * importBundle; null pauses the polling.
 */
export function useImportBundleProgress(jobId: string | null) {
    return trpc.funnels.importBundleProgress.useQuery(
        { jobId: jobId ?? '' },
        {
            enabled: jobId !== null,
            refetchInterval: jobId !== null ? 700 : false,
            refetchIntervalInBackground: true,
            staleTime: 0,
        },
    );
}
